import {UnitReferenceColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  StringResolutionQuery,
  StringResolutionType,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {CustomEval} from 'notebook/tables/TableConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {isTypedValueEmpty, toStringTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {ResolveQueryFn} from 'notebook/tables/typed-values/TypedValueResolution';
import {UnitReferenceTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';

/**
 * Custom eval that provides a default measurement unit for empty cells.
 *
 * Uses the string resolution framework to resolve the unit symbol to a unit reference.
 * The framework handles caching and async fetching automatically.
 */
export function getDefaultUnitCustomEval<CustomEvalOptions>(
  defaultUnitSymbol: string
): CustomEval<CustomEvalOptions> {
  return (
    unitTypedValue: TypedValue,
    columnDefinition: UnitReferenceColumnDefinition,
    cellDependencies: Array<TypedValue | null>,
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn
  ) => {
    // don't override existing values
    if (!isTypedValueEmpty(unitTypedValue)) {
      return {val: unitTypedValue, error: null, locked: false};
    }

    const resolutionQuery: StringResolutionQuery = {
      string: defaultUnitSymbol,
      type: ResolutionQueryType.STRING,
      valueType: {
        type: StringResolutionType.UNIT_REFERENCE,
      },
    };

    const {resolvedValue, error} = resolveFromResolutionQuery(
      toStringTypedValue(defaultUnitSymbol),
      resolutionQuery,
      (resolvedValue: UnitReferenceTypedValue): UnitReferenceTypedValue => resolvedValue
    );

    return {val: resolvedValue, error, locked: false};
  };
}
